/**
 * Counts recorded time only.
 *
 * A recording can be paused, and a paused stretch is not part of the audio
 * file. The duration sent to the API is therefore the sum of the active
 * segments, never the wall-clock span between start and stop.
 */
export interface RecordingClock {
  /** Time already recorded, in the segments that are closed. */
  accumulatedMs: number;
  /** When the open segment started, or null while paused. */
  segmentStartedAt: number | null;
}

export function startRecordingClock(now: number): RecordingClock {
  return { accumulatedMs: 0, segmentStartedAt: now };
}

/** Closes the open segment. Pausing twice changes nothing. */
export function pauseRecordingClock(
  clock: RecordingClock,
  now: number,
): RecordingClock {
  if (clock.segmentStartedAt === null) return clock;
  return {
    accumulatedMs: clock.accumulatedMs + Math.max(0, now - clock.segmentStartedAt),
    segmentStartedAt: null,
  };
}

/** Opens a new segment. Resuming while recording changes nothing. */
export function resumeRecordingClock(
  clock: RecordingClock,
  now: number,
): RecordingClock {
  if (clock.segmentStartedAt !== null) return clock;
  return { accumulatedMs: clock.accumulatedMs, segmentStartedAt: now };
}

/** Recorded time so far, paused stretches excluded. */
export function recordedMs(clock: RecordingClock, now: number): number {
  if (clock.segmentStartedAt === null) return clock.accumulatedMs;
  return clock.accumulatedMs + Math.max(0, now - clock.segmentStartedAt);
}
